import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useComplianceCountry } from '../../../hooks/useComplianceCountry';
import { getCchiPolicy } from '../../../api/Payroll/cchi';
import { EmployeeHealthInsuranceView } from '../../../models/payroll';
import CchiPolicyEditor from './CchiPolicyEditor';

interface CchiPolicySectionProps {
  userId: string;
  isSaudi: boolean;
  canEdit: boolean;
}

export default function CchiPolicySection({ userId, isSaudi, canEdit }: CchiPolicySectionProps) {
  const { t } = useTranslation();
  const complianceCountry = useComplianceCountry();
  const [policy, setPolicy] = useState<EmployeeHealthInsuranceView | null>(null);
  const [loading, setLoading] = useState(true);
  const [editorMode, setEditorMode] = useState<'create' | 'edit' | 'renew' | null>(null);

  useEffect(() => {
    if (complianceCountry !== 'KSA' || isSaudi || !userId) return;
    let cancelled = false;
    setLoading(true);
    getCchiPolicy(userId)
      .then(res => { if (!cancelled) setPolicy(res.errors?.length ? null : res.payload ?? null); })
      // A 404 ("CCHI policy not found") is the normal "no policy yet" state for a non-Saudi
      // employee — the request interceptor rejects on any non-2xx, so without this catch the
      // rejection is unhandled (it surfaces as the webpack dev error overlay and blocks the
      // page). Treat any load failure as "no policy" → the component shows the Add-policy state.
      .catch(() => { if (!cancelled) setPolicy(null); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [userId, complianceCountry, isSaudi]);

  if (complianceCountry !== 'KSA' || isSaudi) return null;

  const today = new Date();
  const expiry = policy?.expiryDate ? new Date(policy.expiryDate) : null;
  const isExpired = expiry !== null && expiry < today;
  const daysUntilExpiry = expiry ? Math.ceil((expiry.getTime() - today.getTime()) / 86400000) : null;

  const cchiStatus = (() => {
    if (!policy) return { label: t('profile.cchi.status.noPolicy', 'No Policy'), color: 'bg-red-100 text-red-800' };
    if (policy.isWaived) return { label: t('profile.cchi.status.waived', 'Waived'), color: 'bg-slate-200 text-slate-700' };
    if (isExpired) return { label: t('profile.cchi.status.expired', 'Expired'), color: 'bg-red-200 text-red-900' };
    if (daysUntilExpiry !== null && daysUntilExpiry <= 7) return { label: t('profile.cchi.status.expiringSoon', 'Expiring Soon'), color: 'bg-red-100 text-red-800' };
    if (daysUntilExpiry !== null && daysUntilExpiry <= 30) return { label: t('profile.cchi.status.expiringSoon', 'Expiring Soon'), color: 'bg-orange-100 text-orange-800' };
    if (daysUntilExpiry !== null && daysUntilExpiry <= 60) return { label: t('profile.cchi.status.expiringSoon', 'Expiring Soon'), color: 'bg-amber-100 text-amber-800' };
    return { label: t('profile.cchi.status.active', 'Active'), color: 'bg-emerald-100 text-emerald-800' };
  })();

  return (
    <div className="rounded-lg border border-border bg-card p-4 space-y-3">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <h3 className="text-sm font-semibold">{t('profile.cchi.section')}</h3>
          {!loading && (
            <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-medium ${cchiStatus.color}`}>
              {cchiStatus.label}
            </span>
          )}
        </div>
        {canEdit && policy && !policy.isWaived && (
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => setEditorMode('edit')}
              className="px-3 py-1 text-xs rounded border border-border hover:bg-muted"
            >
              {t('profile.cchi.actions.edit')}
            </button>
            <button
              type="button"
              onClick={() => setEditorMode('renew')}
              className="px-3 py-1 text-xs rounded border border-primary text-primary hover:bg-primary/10"
            >
              {t('profile.cchi.actions.renew')}
            </button>
          </div>
        )}
        {canEdit && !policy && !loading && (
          <button
            type="button"
            onClick={() => setEditorMode('create')}
            className="px-3 py-1 text-xs rounded border border-primary text-primary hover:bg-primary/10"
          >
            {t('profile.cchi.actions.addPolicy')}
          </button>
        )}
      </div>

      {loading && <div className="text-xs text-muted-foreground">{t('common.loading')}</div>}

      {!loading && !policy && (
        <div className="text-xs text-muted-foreground">
          {canEdit
            ? t('profile.cchi.noPolicy')
            : t('profile.cchi.noPolicyEmployee', 'No health insurance policy on file. Contact HR if you believe this is incorrect.')}
        </div>
      )}

      {!loading && policy && (
        <>
          {policy.tierWarning && (
            <div className="rounded bg-amber-50 px-2 py-1 text-xs text-amber-900">
              {t('payroll.cchi.editor.tierMismatch', {
                expectedTier: policy.tierWarning.expectedTier,
                actualTier: policy.tierWarning.actualTier,
              })}
            </div>
          )}
          {/* CO-269 — neutral, NOT an error: the expected-tier check could not run for this
              employee, so nothing is known about compliance either way. Muted tone on purpose. */}
          {policy.expectedTierUnknown && (
            <div
              data-testid="cchi-expected-tier-unknown"
              className="rounded border border-border bg-muted px-2 py-1 text-xs text-muted-foreground"
            >
              <div className="font-medium text-foreground">{t('payroll.cchi.expectedTierUnknown')}</div>
              <div className="mt-0.5">{t('payroll.cchi.expectedTierUnknownHint')}</div>
            </div>
          )}
          <dl className="grid grid-cols-2 gap-x-4 gap-y-2 text-xs">
            <dt className="text-muted-foreground">{t('payroll.cchi.editor.fields.provider')}</dt>
            <dd>{policy.providerName ?? '—'}</dd>
            <dt className="text-muted-foreground">{t('payroll.cchi.editor.fields.policyNumber')}</dt>
            <dd>{policy.policyNumber ?? '—'}</dd>
            <dt className="text-muted-foreground">{t('payroll.cchi.editor.fields.planTier')}</dt>
            <dd>{policy.planTier ? t(`payroll.cchi.planTier.${policy.planTier}`) : '—'}</dd>
            <dt className="text-muted-foreground">{t('payroll.cchi.editor.fields.effectiveDate')}</dt>
            <dd>{policy.effectiveDate?.slice(0, 10) ?? '—'}</dd>
            <dt className="text-muted-foreground">{t('payroll.cchi.editor.fields.expiryDate')}</dt>
            <dd className={isExpired ? 'text-destructive' : ''}>{policy.expiryDate?.slice(0, 10) ?? '—'}</dd>
            <dt className="text-muted-foreground">{t('payroll.cchi.editor.fields.monthlyPremium')}</dt>
            <dd>{policy.monthlyPremium ?? '—'}</dd>
            <dt className="text-muted-foreground">{t('payroll.cchi.editor.fields.employeePremiumShare')}</dt>
            <dd>{policy.employeePremiumShare ?? '—'}</dd>
          </dl>
          {policy.isWaived && (
            <div className="rounded bg-amber-50 px-2 py-1 text-xs text-amber-900">
              {t('payroll.cchi.editor.fields.waiverReason')}: {policy.waiverReason}
            </div>
          )}
          {policy.policyDocumentUrl && (
            <a
              href={policy.policyDocumentUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-block text-xs text-primary hover:underline"
            >
              {t('payroll.cchi.editor.fields.documentUpload')} ↗
            </a>
          )}
        </>
      )}

      {editorMode !== null && (
        <CchiPolicyEditor
          userId={userId}
          mode={editorMode}
          initial={policy}
          onClose={() => setEditorMode(null)}
          onSaved={updated => { setPolicy(updated); setEditorMode(null); }}
        />
      )}
    </div>
  );
}
